import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Facebook, Instagram, MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AutomationsNetwork = 'whatsapp' | 'instagram' | 'facebook';

/**
 * A single tab in the workspace strip. `TId` defaults to `AutomationsNetwork`
 * so the Automations page keeps its existing typing, but the Forms workspace
 * reuses the same chrome with its own id union (`forms` | `submissions`).
 */
export type AutomationsNetworkTab<TId extends string = AutomationsNetwork> = {
  id: TId;
  label: string;
  count?: number;
  Icon: ComponentType<{ className?: string }>;
  show: boolean;
};

export const AUTOMATIONS_NETWORK_META: Record<
  AutomationsNetwork,
  { label: string; Icon: ComponentType<{ className?: string }> }
> = {
  whatsapp: { label: 'WhatsApp', Icon: MessageCircle },
  instagram: { label: 'Instagram', Icon: Instagram },
  facebook: { label: 'Facebook', Icon: Facebook },
};

export function parseAutomationsNetwork(raw: string | null | undefined): AutomationsNetwork {
  if (raw === 'instagram' || raw === 'facebook' || raw === 'whatsapp') return raw;
  return 'whatsapp';
}

/**
 * Per-platform filter query keys for `/home/automation/flows`.
 *
 * Root cause of cross-tab bleed: Instagram/Facebook previously wrote bare
 * `status` / `q`, which WhatsApp also reads as `status` (and which IG↔FB
 * shared with each other). Namespaced keys keep each network's filters
 * independent; switching `platform` must NOT wipe another network's bag.
 */
export const AUTOMATIONS_FILTER_PARAMS = {
  whatsapp: {
    search: 'search',
    status: 'status',
    trigger: 'trigger',
    page: 'page',
    limit: 'limit',
  },
  instagram: {
    search: 'ig_q',
    status: 'ig_status',
    trigger: 'ig_trigger',
    page: 'ig_page',
    limit: 'ig_limit',
  },
  facebook: {
    search: 'fb_q',
    status: 'fb_status',
    trigger: 'fb_trigger',
  },
} as const;

/** Legacy IG/FB search key before namespacing — never used by WhatsApp. */
export const AUTOMATIONS_LEGACY_SHARED_SEARCH_PARAM = 'q';

type AutomationsWorkspaceShellProps<TId extends string = AutomationsNetwork> = {
  network: TId;
  tabs: AutomationsNetworkTab<TId>[];
  onNetworkChange: (network: TId) => void;
  /**
   * Card-Theme filter row under the tabs (search / Status / Trigger / Create).
   * Matches Social Posts + CRM — no page title above the tabs.
   */
  filterBar?: ReactNode;
  /**
   * True-empty surfaces (no account / zero flows, no active filters) hide the
   * tab strip so the empty state is full-bleed. Callers keep tabs when another
   * network still has a connected account.
   */
  hideTabs?: boolean;
  /**
   * Right icon rail (56px). Rendered as a sibling of the WHOLE workspace column
   * — tabs strip + filter bar + content — so its left divider runs the full
   * height from the tabs row down, matching the reference design. Nesting it
   * beside the content only (inside `children`) starts the divider below the
   * filter bar and leaves it half-drawn on short pages.
   */
  rightRail?: ReactNode;
  children: ReactNode;
};

/**
 * Card-Theme chrome for Automations / Forms:
 * network tabs (CRM / Social underline) + optional filter bar. No page H1 —
 * title lives in the sidebar only (screenshot parity).
 */
export function AutomationsWorkspaceShell<TId extends string = AutomationsNetwork>({
  network,
  tabs,
  onNetworkChange,
  filterBar,
  hideTabs = false,
  rightRail,
  children,
}: AutomationsWorkspaceShellProps<TId>) {
  const visibleTabs = hideTabs ? [] : tabs.filter((t) => t.show);

  // Same mobile overflow pattern as Social Media Posts network tabs: swipe to
  // reach Facebook past Instagram, edge fades when more is off-screen, active
  // tab centers itself so neighbors aren't "invisible".
  const networkTabsScrollRef = useRef<HTMLDivElement>(null);
  const [networkTabsOverflow, setNetworkTabsOverflow] = useState({ left: false, right: false });
  const updateNetworkTabsOverflow = useCallback(() => {
    const el = networkTabsScrollRef.current;
    if (!el) {
      setNetworkTabsOverflow({ left: false, right: false });
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setNetworkTabsOverflow({
      left: scrollLeft > 2,
      right: scrollLeft + clientWidth < scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    const el = networkTabsScrollRef.current;
    if (!el) return;
    updateNetworkTabsOverflow();
    el.addEventListener('scroll', updateNetworkTabsOverflow, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateNetworkTabsOverflow) : null;
    ro?.observe(el);
    window.addEventListener('resize', updateNetworkTabsOverflow);
    return () => {
      el.removeEventListener('scroll', updateNetworkTabsOverflow);
      ro?.disconnect();
      window.removeEventListener('resize', updateNetworkTabsOverflow);
    };
  }, [updateNetworkTabsOverflow, visibleTabs.length]);

  useEffect(() => {
    const root = networkTabsScrollRef.current;
    if (!root) return;
    const active = root.querySelector<HTMLElement>('[data-network-tab-active="true"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    const t = window.setTimeout(updateNetworkTabsOverflow, 350);
    return () => window.clearTimeout(t);
  }, [network, updateNetworkTabsOverflow, visibleTabs.length]);

  return (
    // Outer ROW: workspace column + optional right rail. min-h-full (not h-full
    // + nested overflow): AppShell keeps #app-main-scroll as the scroll owner
    // for Automations/Forms; CRM boards differ there only. The rail is a direct
    // child here so it stretches the full row height.
    <div className="flex min-h-full bg-white">
      <div className="flex min-w-0 flex-1 flex-col bg-white">
      {visibleTabs.length > 0 ? (
        <div className="border-b border-[#EAECF0] bg-white px-[15px] pt-3">
          <div className="relative">
            {networkTabsOverflow.left ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-7 bg-gradient-to-r from-white via-white/90 to-transparent sm:hidden"
              />
            ) : null}
            {networkTabsOverflow.right ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-7 bg-gradient-to-l from-white via-white/90 to-transparent sm:hidden"
              />
            ) : null}
            <div
              ref={networkTabsScrollRef}
              data-testid="automations-network-tabs"
              className="flex items-end gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain scrollbar-hide touch-pan-x [-webkit-overflow-scrolling:touch]"
            >
              {visibleTabs.map((t) => {
                const active = t.id === network;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-network-tab-active={active ? 'true' : undefined}
                    onClick={() => onNetworkChange(t.id)}
                    className={cn(
                      'relative inline-flex items-center gap-2 whitespace-nowrap px-3 pb-3 pt-2 text-[13.5px] font-semibold transition',
                      active ? 'text-[#101828]' : 'text-[#6B7280] hover:text-[#101828]',
                    )}
                  >
                    <t.Icon
                      className={cn('h-4 w-4 shrink-0', active ? 'text-[#475467]' : 'text-[#98A2B3]')}
                    />
                    {t.label}
                    {typeof t.count === 'number' ? (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-[1px] text-[11px] font-semibold',
                          active ? 'bg-[#E7F7EC] text-[#076A2D]' : 'bg-[#F2F4F7] text-[#6B7280]',
                        )}
                      >
                        {t.count}
                      </span>
                    ) : null}
                    {active ? (
                      <span
                        aria-hidden
                        className="absolute bottom-[-1px] left-3 right-3 h-[2px] rounded-t bg-[#0EB64F]"
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

        {filterBar}

        <div className="flex flex-1 flex-col">{children}</div>
      </div>

      {rightRail}
    </div>
  );
}
